/**
 * demo-review-2026-09-09 item 6, resolved 2026-09-09: hiding nav items that
 * are empty by design (CRM/HRM/Billing/Review queue) was deliberately NOT
 * done as a blanket pass in that review — those are real working features
 * other customers use, not broken code, so trimming navigation product-wide
 * to fit one demo tenant's seed state was flagged rather than acted on.
 *
 * Decided: demo tenants only, not product-wide. This module is the org-id
 * allowlist that scopes it — a real customer's nav is never touched by it,
 * whatever their data looks like.
 */

// The four seeded demo personas (scripts/seed_demo.py, scripts/seed_personas.py).
export const DEMO_ORG_IDS = new Set([
  'demo-org-copper-kitchen', // Saffron House
  'demo-org-kiln-cafe',      // Kiln & Co Coffee
  'demo-org-harbour-vine',   // Harbour & Vine Group
  'demo-org-fallow-arms',    // The Fallow Arms
]);

export function isDemoTenantOrg(orgId) {
  return !!orgId && DEMO_ORG_IDS.has(orgId);
}

/**
 * A nav item is hidden only if it opts in (`hideForDemoTenants: true`) AND
 * the current org is one of the seeded demo tenants above. Any other nav
 * item, and any real customer org, is unaffected.
 */
export function shouldHideNavItem(item, orgId) {
  return !!item.hideForDemoTenants && isDemoTenantOrg(orgId);
}

// ---------------------------------------------------------------------------------------------------------------
// The near-live "Thames Tiffin" demo group: a menu per persona, and where each persona lands after signing in.
// Same rule as above: scoped by org id, so a real customer's navigation is never touched.
export const NEAR_LIVE_ORG_IDS = new Set(['demo-org-tt-group', 'demo-org-tt-entity', 'demo-org-tt-franchisee']);
export const FRANCHISEE_ORG_ID = 'demo-org-tt-franchisee';
NEAR_LIVE_ORG_IDS.forEach((id) => DEMO_ORG_IDS.add(id));

// Tooling and marketing: not part of any restaurant persona's day.
const NOISE = new Set([
  'nav-run-service-onboarding', 'nav-run-service-first-run', 'nav-run-service-n8n', 'nav-run-service-demos',
  'nav-run-service-process-discovery', 'nav-stay-compliant-case-studies', 'nav-settings-superadmin',
  'nav-settings-implementations', 'nav-settings-proposals', 'nav-settings-persona', 'nav-cut-cost-calculator',
  'nav-make-money-voice', 'nav-make-money-directconnect', 'nav-make-money-events', 'nav-make-money-event-pro',
  'nav-make-money-rooms', 'nav-make-money-delivery', 'nav-stay-compliant-contractors',
]);
// Managers and kitchen staff: receiving, stock, waste, production, today's alerts.
const OPERATOR_ALLOW = new Set([
  'nav-cut-cost-chef-confirm', 'nav-cut-cost-recipes', 'nav-cut-cost-inventory', 'nav-cut-cost-ck-ops',
  'nav-receiving-settings', 'nav-receiving-queue', 'nav-receiving-inbox', 'nav-receiving-reviews',
  'nav-receiving-putaway', 'nav-receiving-claims', 'nav-receiving-sync', 'nav-cut-cost-waste',
  'nav-cut-cost-procurement', 'nav-cut-cost-forecast', 'nav-cut-cost-demand-forecast', 'nav-make-money-close',
  'nav-make-money-kds', 'nav-run-service-workforce', 'nav-stay-compliant-alerts', 'nav-stay-compliant-documents',
  'nav-stay-compliant-controls',
]);
// The franchisee orders from the CK and sees its own paperwork.
const FRANCHISEE_ALLOW = new Set([
  'nav-cut-cost-marketplace', 'nav-stay-compliant-alerts', 'nav-stay-compliant-documents',
]);

const has = (roles, ...wanted) => (roles || []).some((r) => wanted.includes(r));

export function personaNavVisible(item, orgId, roles) {
  if (!NEAR_LIVE_ORG_IDS.has(orgId)) return true;
  if (NOISE.has(item.testid)) return false;
  if (orgId === FRANCHISEE_ORG_ID) return FRANCHISEE_ALLOW.has(item.testid);
  if (has(roles, 'org_owner', 'org_admin', 'owner', 'admin')) return true;
  // A manager fulfils the franchisees' orders from the CK; the screen's own role checks decide who may act.
  if (item.testid === 'nav-cut-cost-marketplace-ops' && has(roles, 'manager')) return true;
  return OPERATOR_ALLOW.has(item.testid);
}

export const CK_LOCATION_ID = 'demo-loc-tt-ck';

/**
 * Where a persona starts: the screen that holds their day's decisions.
 * owner -> the group view; finance -> cost intelligence; the CK manager -> CK operations; a site manager ->
 * today's alerts; kitchen staff -> chef confirm; the franchisee -> ordering from the CK.
 * ``locationId`` (the login's default site) is what tells a CK manager from a site manager.
 */
export function landingFor(orgId, roles, locationId) {
  if (!NEAR_LIVE_ORG_IDS.has(orgId)) return null;
  if (orgId === FRANCHISEE_ORG_ID) return '/marketplace';
  if (has(roles, 'org_owner', 'owner')) return '/group';
  if (has(roles, 'org_admin', 'admin')) return '/cost-intel';
  if (has(roles, 'manager')) return locationId === CK_LOCATION_ID ? '/ck-ops' : '/alerts';
  return '/chef-confirm';
}
